import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { passwordChangeActions, passwordChangeSelectors } from '../reducer/password-change-reducer';
import styled from '@emotion/styled';
import {
  appMainBackground,
  darkRed,
  defaultBoxShadow,
  white
} from '../../../styles/colors';
import { lionSelectors, LionTranslate } from '@rosetta/react-lion';
import { LaunchpadButton } from '../../../styles/LaunchpadButton';
import { LinkButton } from '../../../styles/LinkButton';
import { InputWithIconContainer as InputWithIcon } from '../../app/util/InputWithIcon';
import { appActions, appSelectors, TRANSACTION_STATE } from '../../app/reducer/app-reducer';
import { MouseCatcherContainer as MouseCatcher } from '../../app/util/MouseCatcher';
import { validateUserInfo } from '../../app/util/user-helper';
import { InputAndAlertContainerContainer as InputAndAlertContainer } from '../../app/util/InputAndAlertContainer';
import { withWindowDimensions } from '@rosetta/react-common-ui';

import { isMobile as isMobileDetected } from 'react-device-detect';

let boxSidePadding = 30;

const ForgotPasswordWrapper = styled.div((props) => {
  let style = {
    position: !props.isMobile ? 'absolute' : 'relative',
    top: '0px',
    bottom: '0px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appMainBackground
  };
  if (props.isMobile) {
    style.minHeight = props.windowHeight - 250 + 'px';
  }
  return style;
});

const ForgotPasswordHolder = styled.div((props) => {
  let style = {
    backgroundColor: !props.isMobile ? white : appMainBackground,
    paddingTop: '30px',
    paddingBottom: '30px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: !props.isMobile ? defaultBoxShadow : 'none',
    borderRadius: '4px'
  };
  return style;
});

const ForgotPasswordHeading = styled.div((props) => {
  let style = {
    fontSize: !props.isMobile ? '30px' : '22px',
    fontWeight: '200',
    letterSpacing: '1px',
    lineHeight: '1.1',
    marginLeft: boxSidePadding,
    marginRight: boxSidePadding,
    textAlign: 'center'
  };
  return style;
});

const ForgotPasswordInstructions = styled.div((props) => {
  let style = {
    fontSize: '14px',
    fontWeight: '300',
    lineHeight: '18px',
    margin: '40px ' + boxSidePadding + 'px 12px',
    textAlign: !props.isMobile ? 'left' : 'center'
  };
  return style;
});

const EmailSentContainer = styled.div({
  marginLeft: boxSidePadding,
  marginRight: boxSidePadding,
  padding: '30px 0 15px'
});

const ForgotPasswordEmailInstructions = styled.div({
  fontSize: '14px',
  fontWeight: '300',
  lineHeight: '22px',
  marginBottom: '20px'
});

const ForgotPasswordSpamInstructions = styled.div({
  fontSize: '12px',
  fontWeight: '300',
  lineHeight: '18px',
  marginBottom: '10px',
  marginTop: '10px'
});

const ErrorMessage = styled.div(() => {
  let style = {
    color: darkRed,
    fontSize: '12px',
    minHeight: '1px',
    marginLeft: boxSidePadding,
    marginRight: boxSidePadding,
    marginBottom: '10px',
    marginTop: '13px'
  };
  return style;
});

const ButtonsContainer = styled.div(() => {
  let style = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'spaceBetween',
    marginLeft: boxSidePadding,
    marginRight: boxSidePadding
  };
  return style;
});

const SubmitButton = styled(LaunchpadButton)((props) => {
  let style = {
    paddingLeft: '25px',
    paddingRight: '25px',
    marginLeft: boxSidePadding,
    marginRight: boxSidePadding
  };
  if (props.isMobile) {
    style.borderRadius = '4px';
  }
  return style;
});

const CancelLink = styled(LinkButton)((props) => {
  let style = {
    lineHeight: '18px',
    marginTop: '26px'
  };
  return style;
});

const emailInputHeight = 34;

export class ForgotPassword extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      emailAddress: this.props.getUserEmail,
      validatingEmail: this.props.getUserEmail.length > 0
    };
  }

  handleEmailChange = (event) => {
    // they are changing the email so set the transaction to a new one, if they had one
    this.props.setLastTransactionState(TRANSACTION_STATE.NO_TRANSACTION);
    this.setState({
      emailAddress: event.target.value
    });
  };

  handleEmailBlur = (event) => {
    if (this.state.emailAddress.length > 0) {
      this.setState({
        validatingEmail: true
      });
    }
  };

  submitInput = () => {
    if (!this.isSubmitDisabled()) {
      this.props.requestPasswordReset(this.state.emailAddress);
    }
  };

  returnToLogin = () => {
    this.props.logoutUser();
  };

  mapEnterToAction = (event) => {
    if (event.key === 'Enter') {
      this.submitInput();
    }
  };

  emailError = () => {
    return validateUserInfo('email', this.state.emailAddress);
  };

  isSubmitDisabled = () => {
    return this.props.lastTransactionState !== TRANSACTION_STATE.NO_TRANSACTION || this.emailError().length > 0;
  };

  render = () => {
    if (!this.props.lionLoaded) {
      return <MouseCatcher />;
    }
    const isMobile = isMobileDetected;

    if (this.props.lastTransactionState === TRANSACTION_STATE.SUCCESS) {
      return (
        <ForgotPasswordWrapper isMobile={isMobile} windowHeight={this.props.clientHeight}>
          <ForgotPasswordHolder isMobile={isMobile}>
            <ForgotPasswordHeading isMobile={isMobile}>
              <LionTranslate lionkey="forgotpassword_state2_heading" />
            </ForgotPasswordHeading>
            <EmailSentContainer>
              <ForgotPasswordEmailInstructions>
                <LionTranslate lionkey="forgotpassword_state2_check_email_instructions" />
              </ForgotPasswordEmailInstructions>
              <ForgotPasswordSpamInstructions>
                <LionTranslate lionkey="forgotpassword_state2_check_spam_instructions" />
              </ForgotPasswordSpamInstructions>
            </EmailSentContainer>
            <ButtonsContainer>
              <SubmitButton onClick={this.returnToLogin} isMobile={isMobile}>
                {this.props.getLionTranslatedString('forgotpassword_state2_button_ok')}
              </SubmitButton>
            </ButtonsContainer>
          </ForgotPasswordHolder>
        </ForgotPasswordWrapper>
      );
    }

    const localizedPlaceholder = this.props.getLionTranslatedString('login_email_field');

    let errorView = null;
    let errorAlert = null;
    let errorKey = '';
    if (this.props.lastTransactionError.length > 0) {
      errorKey = this.props.lastTransactionError;
    } else if (this.state.validatingEmail) {
      errorKey = this.emailError();
    }
    const hasError = errorKey.length > 0;
    if (hasError) {
      if (errorKey === 'REQUEST_INVALID') {
        errorKey = 'REQUEST_NEW_CODE';
      }
      errorView = <ErrorMessage data-qa="errorMessage">{this.props.getLionTranslatedString(errorKey)}</ErrorMessage>;
    }

    return (
      <ForgotPasswordWrapper isMobile={isMobile} windowHeight={this.props.clientHeight}>
        <ForgotPasswordHolder isMobile={isMobile}>
          <ForgotPasswordHeading isMobile={isMobile}>
            <LionTranslate lionkey="forgotpassword_heading" />
          </ForgotPasswordHeading>
          <ForgotPasswordInstructions isMobile={isMobile}>
            <LionTranslate lionkey="forgotpassword_instructions" />
          </ForgotPasswordInstructions>
          {errorView}
          <InputAndAlertContainer
            dataQa="EmailInputAndAlertContainer"
            displayError={hasError}
            outsideHeight={emailInputHeight}
            marginBottom={20}
            inputSideMargin={boxSidePadding}
          >
            <InputWithIcon
              inputType="text"
              value={this.state.emailAddress}
              autoFocus={true}
              tabIndex={1}
              onChange={this.handleEmailChange}
              onBlur={this.handleEmailBlur}
              onKeyPress={this.mapEnterToAction}
              outsideHeight={emailInputHeight}
              placeholder={localizedPlaceholder}
              displayError={hasError}
            />
            {errorAlert}
          </InputAndAlertContainer>
          <ButtonsContainer>
            <SubmitButton
              data-qa="ResetPasswordButton"
              onClick={this.submitInput}
              disabled={this.isSubmitDisabled()}
              tabIndex={2}
              onKeyPress={this.mapEnterToAction}
              isMobile={isMobile}
            >
              {this.props.getLionTranslatedString('forgotpassword_button_submit')}
            </SubmitButton>
            <CancelLink data-qa="ForgotPasswordCancelLink" onClick={() => this.returnToLogin()}>
              {this.props.getLionTranslatedString('forgotpassword_cancel_link')}
            </CancelLink>
          </ButtonsContainer>
        </ForgotPasswordHolder>
      </ForgotPasswordWrapper>
    );
  };
}

const mapStateToProps = (state, ownprops) => ({
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  getLionTranslatedString: (key) => lionSelectors.getLionTranslatedString(state, key),
  getUserEmail: passwordChangeSelectors.getUserEmail(state),
  lastTransactionError: appSelectors.lastTransactionError(state),
  lastTransactionState: appSelectors.lastTransactionState(state)
});

const wrapActions = {
  setLastTransactionState: appActions.setLastTransactionState,
  logoutUser: appActions.logoutUser,
  requestPasswordReset: passwordChangeActions.requestPasswordReset
};
export const ForgotPasswordContainer = connect(
  mapStateToProps,
  wrapActions
)(withWindowDimensions(ForgotPassword));



// WEBPACK FOOTER //
// ./src/modules/login/components/ForgotPassword.jsx